import * as R from 'ramda';
import { CaptchaConfig, CaptchaProvider, UserFlow, ClientFlowConfigs, SipConfig } from './networkConfig';
import { Routes } from './routes';
import { IdentityStoreEndpoints } from './identityStore';

export type ZendeskConfig = {
  key: string;
  fieldsId: {
    did: string;
    flowId: string;
    validationId: string;
    screenId: string;
  };
};

export type SIPServiceConfig = {
  sipEndpoint: string;
  defaultSipAppId: string;
  defaultTimeout: number;
  token: string;
};

export type CivicAnalyticsServiceConfig = {
  endpoint: string;
  // 'source' is how the analytics-proxy knows where to route our events.
  // Same as the componentTag but renamed now that we're no longer tied to CML / Segment for generic event streams.
  // Since the analytics endpoint can now ingest events from multiple sources (it lives with analytics-proxy), the source name has to be sent through by each client.
  source: string;
};

export type ServiceConfig = {
  configEndpoint: string;
  identityStore: {
    endpoint: string;
  };
  facetecProductionMode: boolean;
  sip: SIPServiceConfig;
  civicAnalytics: CivicAnalyticsServiceConfig;
  gatekeeperNetworkService: {
    endpoint: string;
  };
  gatekeeperFaceIdSearch: {
    endpoint: string;
  };
  walletBalanceService: { endpoint: string };
  conversionService: { endpoint: string };
  validation: {
    authEndpoint: string;
    validationEndpoint: string;
    pollingIntervalInSeconds: number;
    feedbackToken: string;
  };
  sentry: {
    dsn: string;
  };
  retention: {
    retentionEndpoint: string;
  };
  networkRetryConfig: {
    defaultRetries: number;
    userControlledRetries: number;
    retryTimeoutMs: Record<number, number>;
    maxAllowedRetryLoops: number;
  };
  captchaKeys: {
    reCaptchaKey: string;
    hCaptchaKey: string;
  };
  uniqueness: {
    revocationPortalUrl: string;
  };
  civicMeBaseUrl: string;
  civicMeDemoBaseUrl: string;
  civicMeUrlQueryParams: string;
  zendeskWidget: ZendeskConfig;
};

export type Configuration = {
  externalLinks: ExternalLinks;
  services: ServiceConfig;
  routeConfigs: RouteConfig;
  flowConfigs: ClientFlowConfigs;
};

export type ExternalLinks = {
  googleStoreUrl: string;
  appleStoreUrl: string;
  civicSupportLink: string;
  civicSupportIpMismatchLink: string;
  civicWebsiteLink: string;
};

export type RouteConfig = {
  [Routes.AWAITING_TRANSACTION_CONFIRMATION]: {
    firstMessageUpdateDelayMilliseconds: number;
    secondMessageUpdateDelayMilliseconds: number;
    thirdMessageUpdateDelayMilliseconds: number;
  };
};

export const defaultProviderConfig = {
  showSelfDeclaration: true,
};

export const defaultSipProviderConfig = {
  ...defaultProviderConfig,
  providerExplainerRoute: Routes.EXPLAINER_SIP,
};

// The scopeRequestTemplate ref is fetched from the Gatekeeper Network Service at runtime.
// All  scopeRequestTemplate configurations below are for e2e tests used in combination with the legacy Civic app to get around captcha limitations
export const webSipConfig: SipConfig = {
  showSelfDeclaration: false,
  route: Routes.VALIDATION_NEXT_STEP,
  credentialsIssuerDid: 'did:sol:tid652xmv91UHLW3HKnQSYMoNYko6FWd8sUEuYF5LPn',
};

const defaultFlowConfig = {
  issuance: {
    route: Routes.VALIDATION_NEXT_STEP,
    providers: {
      sip: {
        ...defaultSipProviderConfig,
        route: Routes.QR_CODE,
        scopeRequestTemplate: 'gatekeeperProofOfIdentity',
      } as SipConfig,
      web: webSipConfig,
    },
  },
  refresh: {
    route: Routes.VALIDATION_NEXT_STEP,
    providers: {
      faceIdentity: {
        showSelfDeclaration: false,
        route: Routes.VALIDATION_NEXT_STEP,
      },
    },
  },
};

// Default config for the document collection flow
export const documentConfig = R.mergeDeepRight(defaultFlowConfig, {
  issuance: {
    providers: {
      sip: {
        scopeRequestTemplate: 'gatekeeperNileNetwork',
      },
    },
  },
  status: {
    route: Routes.VALIDATION_STATUS,
  },
});

export const defaultConfiguration: Configuration = {
  externalLinks: {
    googleStoreUrl: 'https://l.civic.com/7ORfjRLMg7',
    appleStoreUrl: 'https://l.civic.com/1RP0bpRMg7',
    civicSupportLink: 'https://support.civic.com',
    civicSupportIpMismatchLink:
      'https://support.civic.com/hc/en-us/articles/21128989258263-What-to-do-if-you-re-seeing-an-Unable-to-proceed-error',
    civicWebsiteLink: 'https://www.civic.com/',
  },
  services: {
    identityStore: { endpoint: IdentityStoreEndpoints.dev },
    configEndpoint: 'https://dev.api.civic.com/cw-services-dev/browser-configs',
    walletBalanceService: { endpoint: 'https://dev.api.civic.com/balance-lookup-dev' },
    conversionService: {
      endpoint: 'https://dev.api.civic.com/tx-cost-service-dev/conversion',
    },
    sip: {
      sipEndpoint: 'https://preprod.civic.com/sip/v3/scopeRequest',
      defaultSipAppId: '7SXjaG9TH',
      defaultTimeout: 3600 * 24, // 24 hours
      // This value is loaded by dynamic config
      token: 'XXXXXXXXXXXX',
    },
    civicAnalytics: {
      endpoint: 'https://dev.api.civic.com/analytics-dev/events',
      // 'source' is how the analytics-proxy knows where to route our events.
      // Same as the componentTag but renamed now that we're no longer tied to CML / Segment for generic event streams.
      // Since the analytics endpoint can now ingest events from multiple sources (it lives with analytics-proxy), the source name has to be sent through by each client.
      // We still use 'passApi' instead of 'passIframe' until we can migrate the legacy Redshift data.
      // The source currently maps to a Segment source, which is used as the Redshift schema which we are not renaming right now.
      source: 'passApi_dev',
    },
    gatekeeperNetworkService: {
      endpoint: 'https://dev.api.civic.com/gkn-preprod',
    },
    gatekeeperFaceIdSearch: {
      endpoint: 'https://dev-gatekeeper-api.civic.com/v1/record/search',
    },
    validation: {
      // authEndpoint: 'https://d1uez1k2ao6q4u.cloudfront.net', // lambda@edge
      authEndpoint: 'https://dev.api.civic.com/sign-dev', // lambda
      validationEndpoint: 'https://dev.api.civic.com/pass-dev',
      pollingIntervalInSeconds: 5,
      // This value is loaded by dynamic config
      feedbackToken: 'XXXXXXXXXXXX',
    },
    sentry: {
      // This value is loaded by dynamic config
      dsn: '',
    },
    retention: {
      retentionEndpoint: 'https://dev.api.civic.com/pass-retention-service-dev/v0',
    },
    uniqueness: {
      revocationPortalUrl: 'https://revoke-dev.civic.com/uniqueness',
    },
    civicMeBaseUrl: 'https://civic.me',
    civicMeDemoBaseUrl: 'https://demo.civic.me',
    civicMeUrlQueryParams: '?utm_campaign=cpass-iframe&utm_source=cpass&utm_medium=desktop-mobile',
    networkRetryConfig: {
      defaultRetries: 3,
      userControlledRetries: 1,
      retryTimeoutMs: {
        1: 50,
        2: 500,
        3: 2500,
        4: 5000,
      },
      maxAllowedRetryLoops: 5,
    },
    facetecProductionMode: true,
    captchaKeys: {
      reCaptchaKey: '6Lfl-HMdAAAAACZcJGUf6YW0UaWsCFfwUzX07pUH',
      hCaptchaKey: 'c8495527-c4bc-4d02-82c2-8c7658c0685a',
    },
    zendeskWidget: {
      key: '9c97c199-b555-4f22-8487-0d6d1fa0d893',
      fieldsId: {
        did: '1500015937601',
        flowId: '1900007589785',
        validationId: '1500015938441',
        screenId: '1500015996782',
      },
    },
  },
  routeConfigs: {
    [Routes.AWAITING_TRANSACTION_CONFIRMATION]: {
      firstMessageUpdateDelayMilliseconds: 0,
      secondMessageUpdateDelayMilliseconds: 15000,
      thirdMessageUpdateDelayMilliseconds: 15000,
    },
  },
  flowConfigs: {
    [UserFlow.GENESIS]: defaultFlowConfig,
    [UserFlow.BLUE_GENESIS]: defaultFlowConfig,
    [UserFlow.YUKON]: defaultFlowConfig,
    [UserFlow.CAPTCHA]: R.mergeDeepRight(defaultFlowConfig, {
      issuance: {
        route: Routes.EXPLAINER,
        providers: {
          sip: {
            scopeRequestTemplate: 'gatekeeperBasic',
          },
          captcha: {
            showSelfDeclaration: false,
            route: Routes.CAPTCHA,
            captchaProvider: CaptchaProvider.RECAPTCHA,
          } as CaptchaConfig,
          web: undefined,
        },
      },
      refresh: {
        route: Routes.CAPTCHA,
        providers: {
          sip: {
            ...defaultSipProviderConfig,
            route: Routes.QR_CODE,
            scopeRequestTemplate: 'gatekeeperBasic',
          } as SipConfig,
          captcha: {
            route: Routes.CAPTCHA,
            captchaProvider: CaptchaProvider.RECAPTCHA,
          } as CaptchaConfig,
          web: undefined,
        },
      },
    }),
    [UserFlow.AGE_CHECK_13]: {
      issuance: {
        route: Routes.EXPLAINER,
        providers: {
          web: {
            showSelfDeclaration: false,
            route: Routes.DOB_CAPTURE,
          },
        },
      },
    },
    [UserFlow.FACE_AGE_CHECK_13]: {
      issuance: {
        route: Routes.VALIDATION_NEXT_STEP,
        providers: {
          faceIdentity: {
            showSelfDeclaration: false,
            route: Routes.VALIDATION_NEXT_STEP,
          },
        },
      },
    },
    [UserFlow.UNIQUENESS]: {
      issuance: {
        route: Routes.VALIDATION_NEXT_STEP,
        providers: {
          faceUniqueness: {
            showSelfDeclaration: false,
            route: Routes.VALIDATION_NEXT_STEP,
          },
        },
      },
      refresh: {
        route: Routes.VALIDATION_NEXT_STEP,
        providers: {
          faceUniqueness: {
            showSelfDeclaration: false,
            route: Routes.VALIDATION_NEXT_STEP,
          },
        },
      },
      revoke: {
        route: Routes.REVOCATION,
      },
    },
    [UserFlow.LIVENESS]: {
      issuance: {
        route: Routes.VALIDATION_NEXT_STEP,
        providers: {
          faceIdentity: {
            showSelfDeclaration: false,
            route: Routes.VALIDATION_NEXT_STEP,
          },
        },
      },
      refresh: {
        route: Routes.VALIDATION_NEXT_STEP,
        providers: {
          faceIdentity: {
            showSelfDeclaration: false,
            route: Routes.VALIDATION_NEXT_STEP,
          },
        },
      },
    },
    [UserFlow.CIVIC_DEMO]: {
      issuance: {
        route: Routes.EXPLAINER,
        providers: {
          marketing: {
            showSelfDeclaration: false,
            route: Routes.CIVIC_DEMO,
          },
        },
      },
      refresh: {
        route: Routes.EXPLAINER,
        providers: {
          marketing: {
            showSelfDeclaration: false,
            route: Routes.CIVIC_DEMO,
          },
        },
      },
    },
  },
};
